export type {};

declare global {
  interface String {
    /**
     * This is to allow for the decoding of base 64 unicode
     * @see https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
     * @author Chris Hunter
     */
    bzDecode(): string;
    /**
     * This is to allow for the encoding of unicode into base 64
     * @see https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
     * @author Chris Hunter
     */
    bzEncode(): string;
    isEmptyOrWhitespace(): boolean;
    toFloat(): number;
    toInt(radix?: number): number;
    toNumber(radix?: number): number;
    toUint8Array(): Uint8Array;
    splitOnCapitals(): string;
  }

  interface StringConstructor {
    isString(arg: any): arg is string;
  }
}

if (!String.isString) {
  String.isString = function (arg: any): arg is string {
    return typeof arg === 'string';
  };
}

const { prototype } = String;

if (!prototype.bzDecode) {
  Object.defineProperty(prototype, 'bzDecode', {
    writable: false,
    enumerable: false,
    value: function (this: string): string {
      const binString = atob(this);
      const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0)!);
      return new TextDecoder().decode(bytes);
    }
  });
}

if (!prototype.bzEncode) {
  Object.defineProperty(prototype, 'bzEncode', {
    writable: false,
    enumerable: false,
    value: function (this: string): string {
      const bytes = new TextEncoder().encode(this);
      const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');

      return btoa(binString);
    }
  });
}

if (!prototype.isEmptyOrWhitespace) {
  Object.defineProperty(prototype, 'isEmptyOrWhitespace', {
    writable: false,
    enumerable: false,
    value: function (this: string): boolean {
      return !this.trim();
    }
  });
}

if (!prototype.toFloat) {
  Object.defineProperty(prototype, 'toFloat', {
    writable: false,
    enumerable: false,
    value: function (this: string): number {
      return parseFloat(this);
    }
  });
}

if (!prototype.toInt) {
  Object.defineProperty(prototype, 'toInt', {
    writable: false,
    enumerable: false,
    value: function (this: string, radix: number = 10): number {
      return parseInt(this, radix);
    }
  });
}

if (!prototype.toNumber) {
  Object.defineProperty(prototype, 'toNumber', {
    writable: false,
    enumerable: false,
    value: function (this: string, radix: number = 10): number {
      return this.toInt(radix);
    }
  });
}

if (!prototype.splitOnCapitals) {
  Object.defineProperty(prototype, 'splitOnCapitals', {
    writable: false,
    enumerable: false,
    value: function (this: string): string {
      // altered to allow for acronyms e.g. MOT (NextMOTDue => Next MOT Due)
      // return this.split(/(?=(?<=[a-z])[A-Z]|[A-Z](?=[a-z]))/).join(' ');

      // Version of splitOnCapitals compatible for Safari 16.4 and below (see: https://caniuse.com/js-regexp-lookbehind)
      // Please remove this and reinstate the code above if we ever drop Safari 16.4 and below
      return this.replace(/([A-Z]+)([A-Z][a-z])/g, ' $1 $2')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .trim();
    }
  });
}

if (!prototype.toUint8Array) {
  Object.defineProperty(prototype, 'toUint8Array', {
    writable: false,
    enumerable: false,
    value: function (this: string): Uint8Array {
      return new Uint8Array(Array.from(atob(this)).map((char) => char.charCodeAt(0)));
    }
  });
}
